import axios from 'axios'
import { handleUnauthorized, isUnauthorizedError } from './errorInterceptor'
import type {
  LoginCredentials,
  LoginResponse,
  RegisterData,
  RegisterResponse,
  VerifyOTPData,
  ForgotPasswordData,
} from '@walomaydi3/dto'

export interface User {
  userId: string
  email: string
  firstName: string
  lastName: string
  role: 'user' | 'merchant' | 'admin'
  merchantContext?: {
    merchantId: string
    merchantName: string
    permissionLevel: 'owner' | 'admin' | 'manager' | 'viewer'
    permissions: {
      manageUsers: boolean
      manageProducts: boolean
      manageOffers: boolean
      viewReports: boolean
    }
  }
}

// Use proxy configuration from vite.config.ts
const API_BASE_URL = '/api'

const authApi = axios.create({
  baseURL: `${API_BASE_URL}/auth`,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Add request interceptor for authentication
authApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Add response interceptor for error handling
authApi.interceptors.response.use(
  (response) => response,
  (error) => {
    // Handles both 401 and the gateway's 403 for invalid/expired/revoked tokens
    if (isUnauthorizedError(error)) {
      handleUnauthorized()
    }
    return Promise.reject(error)
  }
)

export const authService = {
  // Login user
  async login(credentials: LoginCredentials): Promise<LoginResponse> {
    const response = await authApi.post('/login', credentials)
    
    if (response.data.token) {
      localStorage.setItem('access_token', response.data.token)
      localStorage.setItem('user', JSON.stringify(response.data.user))
    }
    
    return response.data
  },

  // Register new user
  async register(userData: RegisterData): Promise<RegisterResponse> {
    const response = await authApi.post('/register', userData)
    return response.data
  },

  // Verify OTP
  async verifyOTP(otpData: VerifyOTPData): Promise<{ message: string; verified: boolean }> {
    const response = await authApi.post('/verify-otp', otpData)
    return response.data
  },

  // Forgot password
  async forgotPassword(emailData: ForgotPasswordData): Promise<{ message: string }> {
    const response = await authApi.post('/forgot-password', emailData)
    return response.data
  },

  // Logout user
  logout(): void {
    localStorage.removeItem('access_token')
    localStorage.removeItem('user')
    window.location.href = '/login'
  },

  // Get current user from localStorage
  getCurrentUser(): User | null {
    const userStr = localStorage.getItem('user')
    if (!userStr) return null
    
    try {
      return JSON.parse(userStr)
    } catch (error) {
      return null
    }
  },

  // Get auth token
  getToken(): string | null {
    return localStorage.getItem('access_token')
  },

  // Check if user is authenticated (token present and not expired)
  isAuthenticated(): boolean {
    const token = this.getToken()
    if (!token) return false

    try {
      const payload = JSON.parse(
        atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/'))
      )
      // Reject tokens whose expiry date is in the past
      if (payload.exp && payload.exp * 1000 <= Date.now()) {
        localStorage.removeItem('access_token')
        localStorage.removeItem('user')
        return false
      }
    } catch {
      // If the token cannot be decoded, consider it invalid
      return false
    }

    return true
  },

  // Check if user has specific role
  hasRole(role: 'user' | 'merchant' | 'admin'): boolean {
    const user = this.getCurrentUser()
    return user?.role === role
  },

  // Check if user is admin
  isAdmin(): boolean {
    return this.hasRole('admin')
  },

  // Check if user is merchant
  isMerchant(): boolean {
    return this.hasRole('merchant')
  },

  // Check if user has merchant context
  hasMerchantContext(): boolean {
    const user = this.getCurrentUser()
    return !!(user?.merchantContext?.merchantId)
  },

  // Get merchant context
  getMerchantContext() {
    const user = this.getCurrentUser()
    return user?.merchantContext
  },

  // Check if user has specific merchant permission level
  hasMerchantPermissionLevel(requiredLevel: 'owner' | 'admin' | 'manager' | 'viewer'): boolean {
    const user = this.getCurrentUser()
    if (!user?.merchantContext) return false

    const permissionHierarchy = ['viewer', 'manager', 'admin', 'owner']
    const userLevel = user.merchantContext.permissionLevel
    const requiredIndex = permissionHierarchy.indexOf(requiredLevel)
    const userIndex = permissionHierarchy.indexOf(userLevel)

    return userIndex >= requiredIndex
  },

  // Check specific permission
  hasMerchantPermission(permission: 'manageUsers' | 'manageProducts' | 'manageOffers' | 'viewReports'): boolean {
    const user = this.getCurrentUser()
    if (!user?.merchantContext) return false

    return user.merchantContext.permissions[permission] || false
  },

  // RBAC Hierarchical Functions
  // Check if user has access to organization (brand)
  hasOrganizationAccess(_organizationId: string, requiredPermission: 'owner' | 'admin' | 'manager' | 'viewer' = 'viewer'): boolean {
    const user = this.getCurrentUser()
    if (!user?.merchantContext) return false

    // For now, simple check: if user is admin or has merchant context with sufficient permission level
    // In a real implementation, this would call an API to check hierarchical access
    if (this.isAdmin()) return true
    
    // Check if user has the required permission level
    if (!this.hasMerchantPermissionLevel(requiredPermission)) return false

    // TODO: Implement organization-specific access check via API
    // For MVP, assume access if user has merchant context
    return true
  },

  // Check if user has access to store via organization hierarchy
  hasStoreAccess(_storeId: string, requiredPermission: 'owner' | 'admin' | 'manager' | 'viewer' = 'viewer'): boolean {
    const user = this.getCurrentUser()
    if (!user?.merchantContext) return false

    // For now, simple check
    if (this.isAdmin()) return true
    
    if (!this.hasMerchantPermissionLevel(requiredPermission)) return false

    // TODO: Implement store-specific access check via API
    return true
  },

  // Check hierarchical access (organization → store)
  hasHierarchicalAccess(resourceId: string, resourceType: 'organization' | 'store', requiredPermission: 'owner' | 'admin' | 'manager' | 'viewer' = 'viewer'): boolean {
    if (resourceType === 'organization') {
      return this.hasOrganizationAccess(resourceId, requiredPermission)
    } else {
      return this.hasStoreAccess(resourceId, requiredPermission)
    }
  },

  // Get user's accessible organizations (for UI filtering)
  getAccessibleOrganizations(): string[] {
    const user = this.getCurrentUser()
    if (!user?.merchantContext) return []

    // For now, return empty array - would be populated from API
    return []
  },

  // Get user's accessible stores (for UI filtering)
  getAccessibleStores(): string[] {
    const user = this.getCurrentUser()
    if (!user?.merchantContext) return []

    // For now, return empty array - would be populated from API
    return []
  },

  // Validate token (check if expired)
  async validateToken(): Promise<boolean> {
    const token = this.getToken()
    if (!token) return false

    try {
      // In a real implementation, you might want to call a validate endpoint
      // For now, we'll just check if token exists and user is in localStorage
      const user = this.getCurrentUser()
      return !!user
    } catch (error) {
      return false
    }
  },

  // Refresh token (if implemented in backend)
  async refreshToken(): Promise<string | null> {
    try {
      // This would call a refresh token endpoint if implemented
      // For now, we'll just return the current token
      return this.getToken()
    } catch (error) {
      this.logout()
      return null
    }
  }
}

export default authService
